import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import apiService from '../services/apiService';
import {
    X,
    Users,
    Paperclip,
    Send,
    Save,
    Upload,
    Trash2,
    Eye,
    EyeOff
} from 'lucide-react';

const sanitizeMessageHtml = (content = '') => DOMPurify.sanitize(content, {
    ALLOWED_TAGS: ['a', 'b', 'blockquote', 'br', 'code', 'div', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'i', 'img', 'li', 'ol', 'p', 'pre', 'span', 'strong', 'table', 'tbody', 'td', 'th', 'thead', 'tr', 'u', 'ul'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'src', 'alt', 'style'],
    ADD_ATTR: ['target'],
    ADD_TAGS: [],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'svg', 'math']
});

const ComposeMessageModal = ({ isOpen, onClose, onMessageCreated, editingMessage }) => {
    const [formData, setFormData] = useState({
        subject: '',
        body: '',
        excerpt: '',
        type: 'email',
        recipient_type: 'all',
        recipient_criteria: {},
        recipients: [],
        scheduled_at: '',
        attachments: []
    });

    const [recipientOptions, setRecipientOptions] = useState({
        users: [],
        by_role: [],
        by_zone: [],
        stats: {}
    });

    const [selectedUsers, setSelectedUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [recipientOptionsLoading, setRecipientOptionsLoading] = useState(false);
    const [attachmentFiles, setAttachmentFiles] = useState([]);
    const [attachmentsToRemove, setAttachmentsToRemove] = useState([]); // IDs des attachments à supprimer
    const [previewMode, setPreviewMode] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchRecipientOptions();
            
            // Si on édite un message existant, pré-remplir le formulaire
            if (editingMessage) {
                setFormData({
                    subject: editingMessage.subject || '',
                    body: editingMessage.body || '',
                    excerpt: editingMessage.excerpt || '',
                    type: editingMessage.type || 'email',
                    recipient_type: editingMessage.recipient_type || 'all',
                    recipient_criteria: editingMessage.recipient_criteria || {},
                    recipients: editingMessage.recipients || [],
                    scheduled_at: editingMessage.scheduled_at ? 
                        new Date(editingMessage.scheduled_at).toISOString().slice(0, 16) : '',
                    attachments: editingMessage.attachments || []
                });
                
                // Pré-sélectionner les utilisateurs si type custom
                if (editingMessage.recipient_type === 'custom' && editingMessage.recipients) {
                    setSelectedUsers(editingMessage.recipients);
                }
            } else {
                // Réinitialiser pour un nouveau message
                setFormData({
                    subject: '',
                    body: '',
                    excerpt: '',
                    type: 'email',
                    recipient_type: 'all',
                    recipient_criteria: {},
                    recipients: [],
                    scheduled_at: '',
                    attachments: []
                });
                setSelectedUsers([]);
                setAttachmentFiles([]);
                setAttachmentsToRemove([]);
            }
        }
    }, [isOpen, editingMessage]);

    const fetchRecipientOptions = async () => {
        try {
            setRecipientOptionsLoading(true);
            const data = await apiService.getRecipientOptions();
            setRecipientOptions(data);
        } catch (error) {
            console.error('Erreur lors du chargement des destinataires:', error);
        } finally {
            setRecipientOptionsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleRecipientTypeChange = (type) => {
        setFormData(prev => ({
            ...prev,
            recipient_type: type,
            recipient_criteria: {},
            recipients: []
        }));
        setSelectedUsers([]);

        if (type === 'custom') {
            // Ici on pourrait ouvrir une interface de sélection si nécessaire
        }
    };

    const handleRoleSelection = (role, checked) => {
        setFormData(prev => {
            const roles = prev.recipient_criteria.roles || [];
            if (checked) {
                return {
                    ...prev,
                    recipient_criteria: {
                        ...prev.recipient_criteria,
                        roles: [...roles, role]
                    }
                };
            } else {
                return {
                    ...prev,
                    recipient_criteria: {
                        ...prev.recipient_criteria,
                        roles: roles.filter(r => r !== role)
                    }
                };
            }
        });
    };

    const handleZoneSelection = (zone, checked) => {
        setFormData(prev => {
            const zones = prev.recipient_criteria.zones || [];
            if (checked) {
                return {
                    ...prev,
                    recipient_criteria: {
                        ...prev.recipient_criteria,
                        zones: [...zones, zone]
                    }
                };
            } else {
                return {
                    ...prev,
                    recipient_criteria: {
                        ...prev.recipient_criteria,
                        zones: zones.filter(z => z !== zone)
                    }
                };
            }
        });
    };

    const handleUserSelection = (user, checked) => {
        if (checked) {
            setSelectedUsers(prev => [...prev, user]);
        } else {
            setSelectedUsers(prev => prev.filter(u => u.id !== user.id));
        }
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        setAttachmentFiles(prev => [...prev, ...files]);
    };

    const removeAttachment = (index) => {
        setAttachmentFiles(prev => prev.filter((_, i) => i !== index));
    };

    const removeExistingAttachment = (attachmentIndex) => {
        setAttachmentsToRemove(prev => [...prev, attachmentIndex]);
    };

    const calculateRecipientCount = () => {
        switch (formData.recipient_type) {
            case 'all':
                return recipientOptions.stats.total_users || 0;

            case 'role':
                const selectedRoles = formData.recipient_criteria.roles || [];
                return selectedRoles.reduce((total, role) => {
                    return total + (recipientOptions.stats.by_role_count?.[role] || 0);
                }, 0);

            case 'zone':
                const selectedZones = formData.recipient_criteria.zones || [];
                return selectedZones.reduce((total, zone) => {
                    return total + (recipientOptions.stats.by_zone_count?.[zone] || 0);
                }, 0);

            case 'custom':
                return selectedUsers.length;

            default:
                return 0;
        }
    };

    const handleSubmit = async (sendNow = false) => {
        try {
            setLoading(true);

            // Déterminer l'action basée sur les paramètres
            let action = 'save_draft';
            if (sendNow) {
                action = 'send_now';
            } else if (formData.scheduled_at) {
                action = 'schedule';
            }

            // Préparer les données
            const submitData = {
                ...formData,
                action: action,
                recipients: formData.recipient_type === 'custom'
                    ? selectedUsers.map(u => ({ id: u.id, email: u.email, name: `${u.first_name} ${u.last_name}` }))
                    : []
            };

            // Créer FormData pour gérer les fichiers
            const formDataToSend = new FormData();

            // Ajouter les données du message
            Object.keys(submitData).forEach(key => {
                if (key === 'recipient_criteria' || key === 'recipients') {
                    formDataToSend.append(key, JSON.stringify(submitData[key]));
                } else {
                    formDataToSend.append(key, submitData[key]);
                }
            });

            // Ajouter les index des attachments à supprimer (en mode édition seulement)
            if (editingMessage && attachmentsToRemove.length > 0) {
                formDataToSend.append('remove_attachments', JSON.stringify(attachmentsToRemove));
            }

            // Ajouter les fichiers (seulement les nouveaux fichiers valides)
            const validFiles = attachmentFiles.filter(file => file && file.size > 0);
            validFiles.forEach((file) => {
                formDataToSend.append('attachment_files[]', file);
            });

            let response;
            if (editingMessage) {
                console.log('Modification du message ID:', editingMessage.id);
                console.log('FormData pour modification:', Object.fromEntries(formDataToSend));
                // Mise à jour d'un message existant
                response = await apiService.updateMessage(editingMessage.id, formDataToSend);
            } else {
                console.log('Création d\'un nouveau message');
                // Création d'un nouveau message
                response = await apiService.createMessage(formDataToSend);
            }

            onMessageCreated?.(response.data);
            handleClose();

        } catch (error) {
            console.error('Erreur lors de la sauvegarde du message:', error);
            alert(editingMessage ? 'Erreur lors de la modification du message' : 'Erreur lors de la création du message');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            subject: '',
            body: '',
            excerpt: '',
            type: 'email',
            recipient_type: 'all',
            recipient_criteria: {},
            recipients: [],
            scheduled_at: '',
            attachments: []
        });
        setSelectedUsers([]);
        setAttachmentFiles([]);
        setAttachmentsToRemove([]);
        setPreviewMode(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-white">
                    <h2 className="text-2xl font-bold text-gray-900">
                        {previewMode ? 'Aperçu du Message' : (editingMessage ? 'Modifier le Message' : 'Nouveau Message')}
                    </h2>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => setPreviewMode(!previewMode)}
                            className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                            {previewMode ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                            {previewMode ? 'Édition' : 'Aperçu'}
                        </button>
                        <button
                            onClick={handleClose}
                            className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-hidden flex">
                    {/* Contenu principal */}
                    <div className="flex-1 overflow-y-auto p-6 text-justify">
                        {previewMode ? (
                            // Mode aperçu
                            <div className="space-y-6">
                                <div className="bg-gray-50 rounded-lg p-4 border">
                                    <div className="mb-4">
                                        <h3 className="text-lg font-semibold">Sujet: {formData.subject || 'Sans sujet'}</h3>
                                        <p className="text-sm text-gray-600 mt-2">
                                            Destinataires: {calculateRecipientCount()} personne(s) • Type: {formData.type}
                                        </p>
                                        {formData.scheduled_at && (
                                            <p className="text-sm text-gray-600">
                                                Programmé pour: {new Date(formData.scheduled_at).toLocaleString('fr-FR')}
                                            </p>
                                        )}
                                    </div>

                                    <div className="bg-white rounded-lg p-4 border">
                                        {formData.body ? (
                                            <div dangerouslySetInnerHTML={{ __html: sanitizeMessageHtml(formData.body) }} />
                                        ) : (
                                            <p className="text-gray-400 italic">Aucun contenu saisi</p>
                                        )}
                                    </div>

                                    {attachmentFiles.length > 0 && (
                                        <div className="mt-4">
                                            <h4 className="text-sm font-medium text-gray-700 mb-2">Pièces jointes (Veuillez choisir un pdf):</h4>
                                            <div className="space-y-1">
                                                {attachmentFiles.map((file, index) => (
                                                    <div key={index} className="text-sm text-gray-600 flex items-center">
                                                        <Paperclip className="w-4 h-4 mr-2" />
                                                        {file.name} ({(file.size / 1024).toFixed(1)} KB)
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            // Mode édition
                            <div className="space-y-6">
                                {/* Informations de base */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Sujet du message *
                                        </label>
                                        <input
                                            type="text"
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleInputChange}
                                            placeholder="Entrez le sujet du message..."
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Type de message
                                        </label>
                                        <select
                                            name="type"
                                            value={formData.type}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                        >
                                            <option value="email">Email</option>
                                            <option value="sms">SMS</option>
                                            <option value="notification">Notification</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Extrait */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Extrait (optionnel)
                                    </label>
                                    <input
                                        type="text"
                                        name="excerpt"
                                        value={formData.excerpt}
                                        onChange={handleInputChange}
                                        placeholder="Court résumé du message..."
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                        maxLength={255}
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        {formData.excerpt.length}/255 caractères
                                    </p>
                                </div>

                                {/* Contenu du message */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Contenu du message *
                                    </label>
                                    <textarea
                                        name="body"
                                        value={formData.body}
                                        onChange={handleInputChange}
                                        rows={8}
                                        placeholder="Saisissez le contenu de votre message..."
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-y"
                                        required
                                    />
                                </div>

                                {/* Programmation */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Programmation (optionnel)
                                    </label>
                                    <input
                                        type="datetime-local"
                                        name="scheduled_at"
                                        value={formData.scheduled_at}
                                        onChange={handleInputChange}
                                        min={new Date().toISOString().slice(0, 16)}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Laissez vide pour envoyer immédiatement
                                    </p>
                                </div>

                                {/* Fichiers joints */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Pièces jointes (Veuillez choisir un pdf)
                                    </label>
                                    
                                    {/* Afficher les pièces jointes existantes */}
                                    {editingMessage && editingMessage.attachments && editingMessage.attachments.length > 0 && (
                                        <div className="mb-4">
                                            <h4 className="text-sm font-medium text-gray-600 mb-2">📁 Pièces jointes actuelles:</h4>
                                            <div className="space-y-2 bg-blue-50 rounded-lg p-3">
                                                {editingMessage.attachments.map((attachment, index) => {
                                                    const isMarkedForRemoval = attachmentsToRemove.includes(index);
                                                    return (
                                                        <div key={index} className={`flex items-center justify-between rounded p-2 ${isMarkedForRemoval ? 'bg-red-50 opacity-50' : 'bg-white'}`}>
                                                            <div className="flex items-center">
                                                                <Paperclip className={`w-4 h-4 mr-2 ${isMarkedForRemoval ? 'text-red-400' : 'text-blue-500'}`} />
                                                                <span className={`text-sm ${isMarkedForRemoval ? 'text-red-600 line-through' : 'text-gray-700'}`}>
                                                                    {attachment.name} 
                                                                    <span className="text-xs text-gray-500">
                                                                        ({(attachment.size / 1024).toFixed(1)} KB)
                                                                    </span>
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                {isMarkedForRemoval ? (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setAttachmentsToRemove(prev => prev.filter(i => i !== index))}
                                                                        className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200"
                                                                        title="Annuler la suppression"
                                                                    >
                                                                        Restaurer
                                                                    </button>
                                                                ) : (
                                                                    <>
                                                                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                                                            Existant
                                                                        </span>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => removeExistingAttachment(index)}
                                                                            className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200"
                                                                            title="Supprimer ce fichier"
                                                                        >
                                                                            ✕ Supprimer
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Ajouter de nouvelles pièces jointes */}
                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                                        <input
                                            type="file"
                                            multiple
                                            onChange={handleFileUpload}
                                            className="hidden"
                                            id="file-upload"
                                        />
                                        <label
                                            htmlFor="file-upload"
                                            className="cursor-pointer flex items-center justify-center text-gray-600 hover:text-gray-800"
                                        >
                                            <Upload className="w-5 h-5 mr-2" />
                                            {editingMessage && editingMessage.attachments && editingMessage.attachments.length > 0 
                                                ? 'Ajouter d\'autres fichiers'
                                                : 'Cliquez pour ajouter des fichiers'
                                            }
                                        </label>
                                    </div>

                                    {attachmentFiles.length > 0 && (
                                        <div className="mt-4">
                                            <h4 className="text-sm font-medium text-gray-600 mb-2">🆕 Nouveaux fichiers à ajouter:</h4>
                                            <div className="space-y-2 bg-green-50 rounded-lg p-3">
                                                {attachmentFiles.map((file, index) => (
                                                    <div key={index} className="flex items-center justify-between bg-white rounded p-2">
                                                        <div className="flex items-center">
                                                            <Paperclip className="w-4 h-4 mr-2 text-green-500" />
                                                            <span className="text-sm text-gray-700">
                                                                {file.name} ({(file.size / 1024).toFixed(1)} KB)
                                                            </span>
                                                        </div>
                                                        <button
                                                            onClick={() => removeAttachment(index)}
                                                            className="text-red-600 hover:text-red-800 p-1 rounded"
                                                            title="Supprimer"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Panneau des destinataires */}
                    <div className="w-80 border-l border-gray-200 bg-gray-50 overflow-y-auto">
                        <div className="p-4 border-b border-gray-200 bg-white">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                <Users className="w-5 h-5 mr-2" />
                                Destinataires
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                                {calculateRecipientCount()} personne(s) sélectionnée(s)
                            </p>
                        </div>

                        <div className="p-4 space-y-4">
                            {/* Informations de débogage et statut */}
                            {recipientOptionsLoading && (
                                <div className="text-center py-4">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto mb-2"></div>
                                    <p className="text-sm text-gray-600">Chargement des destinataires...</p>
                                </div>
                            )}

                            {!recipientOptionsLoading && (!recipientOptions.stats?.total_users || recipientOptions.stats.total_users === 0) && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <p className="text-sm text-yellow-700">
                                        ⚠️ Aucun utilisateur trouvé. Vérifiez que des utilisateurs sont bien enregistrés dans la base de données et que le backend est démarré.
                                    </p>
                                </div>
                            )}

                            {/* Types de destinataires */}
                            <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-3">Type de destinataires</h4>
                                <div className="space-y-2">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="recipient_type"
                                            value="all"
                                            checked={formData.recipient_type === 'all'}
                                            onChange={(e) => handleRecipientTypeChange(e.target.value)}
                                            className="text-purple-600 focus:ring-purple-500"
                                        />
                                        <span className="ml-2 text-sm">Tous les utilisateurs ({recipientOptions.stats?.total_users || 0})</span>
                                    </label>

                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="recipient_type"
                                            value="role"
                                            checked={formData.recipient_type === 'role'}
                                            onChange={(e) => handleRecipientTypeChange(e.target.value)}
                                            className="text-purple-600 focus:ring-purple-500"
                                        />
                                        <span className="ml-2 text-sm">Par rôle</span>
                                    </label>

                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="recipient_type"
                                            value="zone"
                                            checked={formData.recipient_type === 'zone'}
                                            onChange={(e) => handleRecipientTypeChange(e.target.value)}
                                            className="text-purple-600 focus:ring-purple-500"
                                        />
                                        <span className="ml-2 text-sm">Par zone</span>
                                    </label>

                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="recipient_type"
                                            value="custom"
                                            checked={formData.recipient_type === 'custom'}
                                            onChange={(e) => handleRecipientTypeChange(e.target.value)}
                                            className="text-purple-600 focus:ring-purple-500"
                                        />
                                        <span className="ml-2 text-sm">Sélection personnalisée</span>
                                    </label>
                                </div>
                            </div>

                            {/* Sélection par rôle */}
                            {formData.recipient_type === 'role' && (
                                <div>
                                    <h5 className="text-sm font-medium text-gray-700 mb-2">Rôles</h5>
                                    <div className="space-y-1 max-h-32 overflow-y-auto">
                                        {(recipientOptions.by_role || []).map((roleData) => (
                                            <label key={roleData.role} className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.recipient_criteria.roles?.includes(roleData.role) || false}
                                                    onChange={(e) => handleRoleSelection(roleData.role, e.target.checked)}
                                                    className="text-purple-600 focus:ring-purple-500"
                                                />
                                                <span className="ml-2 text-sm">
                                                    {roleData.role} ({roleData.count})
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Sélection par zone */}
                            {formData.recipient_type === 'zone' && (
                                <div>
                                    <h5 className="text-sm font-medium text-gray-700 mb-2">Zones</h5>
                                    <div className="space-y-1 max-h-32 overflow-y-auto">
                                        {(recipientOptions.by_zone || []).map((zoneData) => (
                                            <label key={zoneData.zone} className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.recipient_criteria.zones?.includes(zoneData.zone) || false}
                                                    onChange={(e) => handleZoneSelection(zoneData.zone, e.target.checked)}
                                                    className="text-purple-600 focus:ring-purple-500"
                                                />
                                                <span className="ml-2 text-sm">
                                                    {zoneData.zone} ({zoneData.count})
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Sélection personnalisée */}
                            {formData.recipient_type === 'custom' && (
                                <div>
                                    <h5 className="text-sm font-medium text-gray-700 mb-2">Utilisateurs</h5>
                                    <div className="space-y-1 max-h-64 overflow-y-auto">
                                        {recipientOptionsLoading ? (
                                            <div className="text-center py-4">
                                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto"></div>
                                            </div>
                                        ) : (
                                            (recipientOptions.users || []).map((user) => (
                                                <label key={user.id} className="flex items-center p-2 hover:bg-white rounded">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedUsers.some(u => u.id === user.id)}
                                                        onChange={(e) => handleUserSelection(user, e.target.checked)}
                                                        className="text-purple-600 focus:ring-purple-500"
                                                    />
                                                    <div className="ml-2 text-sm">
                                                        <div>{user.first_name} {user.last_name}</div>
                                                        <div className="text-xs text-gray-500">{user.email}</div>
                                                    </div>
                                                </label>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer avec actions */}
                <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50">
                    <div className="text-sm text-gray-600">
                        {calculateRecipientCount()} destinataire(s) • {formData.type}
                        {formData.scheduled_at && ' • Programmé'}
                    </div>

                    <div className="flex space-x-3">
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                            disabled={loading}
                        >
                            Annuler
                        </button>

                        <button
                            onClick={() => handleSubmit(false)}
                            disabled={loading || !formData.subject || !formData.body}
                            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 flex items-center"
                        >
                            <Save className="w-4 h-4 mr-2" />
                            {formData.scheduled_at ? 'Programmer' : 'Sauvegarder brouillon'}
                        </button>

                        <button
                            onClick={() => handleSubmit(true)}
                            disabled={loading || !formData.subject || !formData.body}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center"
                        >
                            <Send className="w-4 h-4 mr-2" />
                            {loading ? 'Envoi...' : 'Envoyer maintenant'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ComposeMessageModal;